/**
 * Centralized route constants for the application
 * Use these constants instead of hardcoded strings to prevent typos and ease refactoring
 */
export const ROUTES = {
  // Public pages
  HOME: "/",
  AUTH: "/autenticacao",
  ACCOUNT_LINKING: "/vincular-conta",
  FORGOT_PASSWORD: "/recuperar-senha",
  RESET_PASSWORD: "/redefinir-senha",
  
  // Browse & Explore
  BROWSE: "/explorar",
  BROWSE_CLASSES: "/explorar-materias",
  
  // New emotional routes
  DISCOVER_EXPERIENCES: "/descobrir-experiencias",
  EXPERIENCE_DETAIL: "/experiencia/:slug",
  MY_SHELF: "/minha-estante",
  EXPERIENCES_FOR_YOU: "/experiencias-para-voce",
  
  // Old routes (kept for backwards compatibility, will redirect)
  EXPLORE_SUBJECTS: "/materias",
  FAVORITE_SUBJECTS: "/materias/favoritas",
  EXPLORED_SUBJECTS: "/materias/avaliadas",
  RECOMMENDED_SUBJECTS: "/materias/recomendadas",
  SEARCH_SUBJECTS: "/buscar-materias",
  
  TEACHER_PROFILE: "/professor/:slugOrId",
  
  // Journey pages
  DASHBOARD: "/dashboard",
  ONBOARDING: "/onboarding",
  JOURNEY_STUDENT: "/minha-jornada-aluno",
  JOURNEY_STUDENT_ALT: "/jornada-aluno",
  JOURNEY_STUDENT_VIEW: "/jornada-aluno/:id",
  JOURNEY_DIARY: "/diario-da-travessia",
  JOURNEY_EDUCATOR: "/minha-jornada-educador",
  JOURNEY_ADMIN: "/minha-jornada-admin",
  JOURNEY_FAMILY: "/minha-jornada-familia",
  
  // New emotional routes for different profiles
  MY_EXPERIENCES: "/minhas-experiencias",  // Family/Student progress & achievements
  MY_CLASSES_CALENDAR: "/minhas-aulas",    // Family calendar & schedule (canonical)
  TEACHER_CLASSES: "/aulas-criadas",        // Teacher classes management
  MY_GROUPS: "/minhas-turmas",              // Teacher recurring groups
  MY_IMPACT: "/meu-impacto",                // Teacher KPIs & performance
  
  // Profile & Settings
  PROFILE: "/perfil",
  HUMANIZED_PROFILE: "/perfil-humanizado",
  TEACHER_BIO: "/bio-educador",
  STUDENT_BIO: "/bio-estudante/:studentId",
  STUDENT_PROFILE: "/aluno/:id",
  MY_FAVORITES: "/meus-favoritos",
  
  // Student management
  MANAGE_STUDENTS: "/gerenciar-estudantes",
  STUDENT_LOGIN: "/student-login",
  STUDENT_DASHBOARD: "/student-dashboard",
  STUDENT_VIEW: "/visualizacao-estudante/:studentId",
  STUDENT_MATERIALS: "/materiais-aluno",
  STUDENT_RECORDINGS: "/gravacoes-aluno",
  
  // LMS 3.0: Learning Paths & Community
  LEARNING_PATHS: "/trilhas",
  COMMUNITY: "/comunidade",
  
  // Certificates & Achievements
  MY_CERTIFICATES: "/meus-certificados",
  ACHIEVEMENTS: "/conquistas",
  
  // Teacher student creations
  TEACHER_STUDENT_CREATIONS: "/galeria-criacoes",
  TEACHER_STUDENT_CREATIONS_VIEW: "/galeria-criacoes/:studentId",
  
  // Teacher Materials
  TEACHER_MATERIALS: "/meus-materiais",

  // Classes & Availability
  CREATE_CLASS: "/minha-jornada/criar-experiencias",
  EDIT_CLASS: "/minha-jornada/editar/:classId",
  CLASS_HISTORY: "/historico-de-encontros",
  MANAGE_AVAILABILITY: "/gerenciar-disponibilidade",
  BOOKING_CALENDAR: "/agendar/:teacherId",
  
  // Admin
  ADMIN: "/admin",
  ADMIN_USERS: "/admin/users",
  ADMIN_CLASSES: "/admin/turmas",
  ADMIN_ANALYTICS: "/admin/analytics",
  ADMIN_SETTINGS: "/admin/configuracoes",
  ADMIN_ONBOARDING_PREVIEW: "/admin/preview-onboarding",
  ADMIN_IMAGE_CURATION: "/admin/curadoria-imagens",
  ADMIN_AVATAR_MANAGER: "/admin/avatar-manager",
  ADMIN_BRAND_ASSETS: "/admin/brand-assets",
  ADMIN_VIDEO_TEST: "/admin/video-test",
  ADMIN_CONCIERGE: "/admin/concierge",
  ADMIN_COMPETITOR_INSIGHTS: "/admin/competitor-insights",
  ADMIN_SEO_PAGES: "/admin/seo-pages",
  
  // Teacher application
  MANIFESTO_TEACHERS: "/quero-ensinar",
  APPLY_TO_TEACH: "/candidatar-se",
  APPLICATION_STATUS: "/application-status",
  
  // Teacher Directory
  FIND_TEACHERS: "/nossos-educadores",
  
  // Footer pages
  HOW_IT_WORKS: "/como-funciona",
  FOR_EDUCATORS: "/para-educadores",
  HELP: "/ajuda",
  CENTRAL_ENCONTROS: "/central-encontros",
  MESSAGES: "/mensagens",
  NOTIFICATIONS: "/notificacoes",
  TERMS_OF_SERVICE: "/termos-de-uso",
  PRIVACY_POLICY: "/politica-de-privacidade",
  ACCOUNT_PRIVACY: "/conta/privacidade",
  ACCOUNT_REMINDERS: "/conta/lembretes",
  
  
  
  // Class & Subject Detail Pages
  CLASS_DETAIL: "/aula/:classId",
  SUBJECT_DETAIL: "/materia/:slugOrId",
  
  // Calendar
  CALENDAR: "/calendario",
  
  // Video Room
  AULA_ROOM: "/sala/:bookingId",
  INSTANT_MEETING: "/encontro/:meetingCode",
  
  // Integrations
  GOOGLE_CALENDAR_CALLBACK: "/google-calendar-callback",

  // Payments
  PAYMENT_SUCCESS: "/pagamento/sucesso",
  PAYMENT_CANCEL: "/pagamento/cancelado",
  EXPRESS_CHECKOUT: "/checkout/:classId",
  
  // Deprecated redirects
  DEPRECATED_TEACHER_SIGNUP: "/cadastro-professor",
  DEPRECATED_FAMILY_SIGNUP: "/cadastro-familia",
  
  // SEO Programmatic Landing Pages (catch-all, must be before NOT_FOUND)
  SEO_LANDING: "/:seoSlug",

  // 404
  NOT_FOUND: "*",
} as const;

/**
 * Helper function to generate dynamic routes
 */
export const generateRoute = {
  teacherProfile: (slugOrId: string) => `/professor/${slugOrId}`,
  studentBio: (studentId: string) => `/bio-estudante/${studentId}`,
  studentProfile: (id: string) => `/aluno/${id}`,
  studentView: (studentId: string) => `/visualizacao-estudante/${studentId}`,
  studentJourneyView: (studentId: string) => `/jornada-aluno/${studentId}`,
  editClass: (classId: string) => `/minha-jornada/editar/${classId}`,
  bookingCalendar: (teacherId: string) => `/agendar/${teacherId}`,
  experienceDetail: (slug: string) => `/experiencia/${slug}`,
  aulaRoom: (bookingId: string, returnUrl?: string) => 
    `/sala/${bookingId}${returnUrl ? `?returnUrl=${encodeURIComponent(returnUrl)}` : ''}`,
  classDetail: (classId: string) => `/aula/${classId}`,
  subjectDetail: (slugOrId: string) => `/materia/${slugOrId}`,
  teacherStudentCreations: (studentId?: string) => 
    studentId ? `/galeria-criacoes/${studentId}` : `/galeria-criacoes`,
  instantMeeting: (meetingCode: string) => `/encontro/${meetingCode}`,
  expressCheckout: (classId: string) => `/checkout/${classId}`,
} as const;
